import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { regionLabel } from '@/utils/normalizeRegion';
import { Language, LocalizedName } from '@/types/resort';
import { SITE, DEFAULT_LANGUAGE, isSiteLanguage, pick, type LocalizedText } from '@/site';

interface Translations {
  // Navigation
  home: LocalizedName;
  webcams: LocalizedName;
  resorts: LocalizedName;
  slopes: LocalizedName;
  weather: LocalizedName;
  about: LocalizedName;
  privacy: LocalizedName;
  
  // Common
  search: LocalizedName;
  filter: LocalizedName;
  sort: LocalizedName;
  viewAll: LocalizedName;
  loading: LocalizedName;
  error: LocalizedName;
  offline: LocalizedName;
  live: LocalizedName;
  
  // Slopes
  difficulty: LocalizedName;
  length: LocalizedName;
  elevation: LocalizedName;
  avgAngle: LocalizedName;
  grade: LocalizedName;
  width: LocalizedName;
  
  // Difficulty labels
  beginner: LocalizedName;
  beginnerIntermediate: LocalizedName;
  intermediate: LocalizedName;
  intermediateAdvanced: LocalizedName;
  advanced: LocalizedName;
  expert: LocalizedName;
  terrainPark: LocalizedName;
  
  // Resort
  lifts: LocalizedName;
  cameras: LocalizedName;
  homepage: LocalizedName;
  
  // Stream warnings
  externalStreamOnly: LocalizedName;
  viewOnOfficialSite: LocalizedName;
  viewWeather: LocalizedName;
  
  // Units
  meters: LocalizedName;
  degrees: LocalizedName;
  percent: LocalizedName;
  perHour: LocalizedName;
  seats: LocalizedName;
  
  // Hero
  heroTitle: LocalizedName;
  exploreWebcams: LocalizedName;
  viewResorts: LocalizedName;
  
  // Stats
  totalResorts: LocalizedName;
  totalWebcams: LocalizedName;
  totalSlopes: LocalizedName;
  totalLifts: LocalizedName;
  
  // Regions
  regions: LocalizedName;
  allRegions: LocalizedName;
  
  // Footer
  copyright: LocalizedName;
  dataSource: LocalizedName;
  
  // Dashboard
  liveCamera: LocalizedName;
  liveCameras: LocalizedName;
  allWebcams: LocalizedName;
  viewResortCameras: LocalizedName;
  viewResortDetails: LocalizedName;
  clickForForecast: LocalizedName;
  fromTokyo: LocalizedName;
  windKmh: LocalizedName;
  feelsLike: LocalizedName;
  precipMm: LocalizedName;
  humidity: LocalizedName;
  visibility: LocalizedName;
  gusts: LocalizedName;
  totalRuns: LocalizedName;
  runs: LocalizedName;
  cams: LocalizedName;
  weatherNote: LocalizedName;
  raceTracksSection: LocalizedName;
  trackLength: LocalizedName;
  turns: LocalizedName;
  circuits: LocalizedName;
  viewTrackCamera: LocalizedName;
  
  // Weather detail labels
  weatherFeels: LocalizedName;
  weatherWind: LocalizedName;
  weatherPrecip: LocalizedName;
  weatherHumid: LocalizedName;
  weatherVis: LocalizedName;
  weatherPressure: LocalizedName;
  weatherGusts: LocalizedName;
  weatherRise: LocalizedName;
  weatherSet: LocalizedName;
  sevenDayForecast: LocalizedName;
  today: LocalizedName;
  
  // Racetrack specific
  racetrackLink: LocalizedName;
  
  // Sorting
  sortBy: LocalizedName;
  sortByName: LocalizedName;
  sortBySnow: LocalizedName;
  sortBySlopes: LocalizedName;
  sortByCameras: LocalizedName;
  sortByLength: LocalizedName;
  sortByTemp: LocalizedName;
  sortByConditions: LocalizedName;
  searchPlaceholder: LocalizedName;
  noResults: LocalizedName;
  
  // Detail pages
  allResorts: LocalizedName;
  backToDashboard: LocalizedName;
  resortNotFound: LocalizedName;
  resortNotFoundDesc: LocalizedName;
  racetrackNotFound: LocalizedName;
  racetrackNotFoundDesc: LocalizedName;
  viewAllResorts: LocalizedName;
  weatherUnavailable: LocalizedName;
  total: LocalizedName;
  noWebcams: LocalizedName;
  likelyCovers: LocalizedName;
  stream: LocalizedName;
  trackSpecs: LocalizedName;
  mainTrackLength: LocalizedName;
  capacity: LocalizedName;
  speed: LocalizedName;
  racetrack: LocalizedName;
  location: LocalizedName;
  
  // SEO intro
  seoIntroHeading: LocalizedName;
  
  // Tooltips
  tooltipNaverMap: LocalizedName;
  tooltipKakaoMap: LocalizedName;
  tooltipGoogleMap: LocalizedName;
  tooltipWeather: LocalizedName;
  tooltipHomepage: LocalizedName;
  tooltipLiveCams: LocalizedName;
  tooltipSlopes: LocalizedName;
  tooltipTotalLength: LocalizedName;
  
  // Snow alerts
  currentlySnowing: LocalizedName;
  snowingToday: LocalizedName;
  freshSnow: LocalizedName;
  lastSnowfall: LocalizedName;
  noFreshSnow: LocalizedName;
  snowDepth: LocalizedName;
  newSnow: LocalizedName;
  bestSnowConditions: LocalizedName;
  bestSnowNow: LocalizedName;
  
  // Resort groups
  resortsInGroup: LocalizedName;
  connectedResorts: LocalizedName;
  area: LocalizedName;
}

// Site-dependent copy: generic templates that render Japan's original strings
// on the jp site; `JP` keeps the few Japan-only facts (JMA, Tokyo).
const JP = SITE.id === 'jp';
const COUNTRY = { ...SITE.country.name, zh: SITE.country.name.zh ?? SITE.country.name.en } as Required<LocalizedText>;
const ADJ = SITE.country.adjective;
const ORIGIN_NAME = SITE.originCity;
const ORIGIN = { ja: ORIGIN_NAME.ja ?? ORIGIN_NAME.en, en: ORIGIN_NAME.en, ko: ORIGIN_NAME.ko ?? ORIGIN_NAME.en, zh: ORIGIN_NAME.zh ?? ORIGIN_NAME.en };

const translations: Translations = {
  // Navigation
  home: { ko: '홈', en: 'Home', ja: 'ホーム', zh: '首页' },
  webcams: { ko: '웹캠', en: 'Webcams', ja: 'ウェブカメラ', zh: '摄像头' },
  resorts: { ko: '리조트', en: 'Resorts', ja: 'リゾート', zh: '度假村' },
  slopes: { ko: '슬로프', en: 'Slopes', ja: 'ゲレンデ', zh: '雪道' },
  weather: { ko: '날씨', en: 'Weather', ja: '天気', zh: '天气' },
  about: { ko: '소개', en: 'About', ja: '概要', zh: '关于' },
  privacy: { ko: '개인정보', en: 'Privacy', ja: 'プライバシー', zh: '隐私' },
  
  // Common
  search: { ko: '검색', en: 'Search', ja: '検索', zh: '搜索' },
  filter: { ko: '필터', en: 'Filter', ja: 'フィルター', zh: '筛选' },
  sort: { ko: '정렬', en: 'Sort', ja: '並び替え', zh: '排序' },
  viewAll: { ko: '전체보기', en: 'View All', ja: 'すべて表示', zh: '查看全部' },
  loading: { ko: '로딩 중...', en: 'Loading...', ja: '読み込み中...', zh: '加载中...' },
  error: { ko: '오류', en: 'Error', ja: 'エラー', zh: '错误' },
  offline: { ko: '오프라인', en: 'Offline', ja: 'オフライン', zh: '离线' },
  live: { ko: '실시간', en: 'LIVE', ja: 'ライブ', zh: '直播' },
  
  // Slopes
  difficulty: { ko: '난이도', en: 'Difficulty', ja: '難易度', zh: '难度' },
  length: { ko: '길이', en: 'Length', ja: '長さ', zh: '长度' },
  elevation: { ko: '표고차', en: 'Elevation', ja: '標高差', zh: '落差' },
  avgAngle: { ko: '평균 경사', en: 'Avg Angle', ja: '平均角度', zh: '平均坡度' },
  grade: { ko: '경사도', en: 'Grade', ja: '勾配', zh: '坡度' },
  width: { ko: '폭', en: 'Width', ja: '幅', zh: '宽度' },
  
  // Difficulty labels
  beginner: { ko: '초급', en: 'Beginner', ja: '初級', zh: '初级' },
  beginnerIntermediate: { ko: '초중급', en: 'Beginner-Intermediate', ja: '初中級', zh: '初中级' },
  intermediate: { ko: '중급', en: 'Intermediate', ja: '中級', zh: '中级' },
  intermediateAdvanced: { ko: '중상급', en: 'Intermediate-Advanced', ja: '中上級', zh: '中高级' },
  advanced: { ko: '상급', en: 'Advanced', ja: '上級', zh: '高级' },
  expert: { ko: '최상급', en: 'Expert', ja: 'エキスパート', zh: '专家级' },
  terrainPark: { ko: '파크', en: 'Terrain Park', ja: 'パーク', zh: '地形公园' },
  
  // Resort
  lifts: { ko: '리프트', en: 'Lifts', ja: 'リフト', zh: '缆车' },
  cameras: { ko: '카메라', en: 'Cameras', ja: 'カメラ', zh: '摄像头' },
  homepage: { ko: '리조트 홈페이지', en: 'Resort Homepage', ja: 'リゾートホームページ', zh: '度假村官网' },
  viewWeather: JP
    ? { ja: 'JMA公式天気', en: 'JMA Official Weather', ko: 'JMA 공식 날씨', zh: 'JMA官方天气' }
    : { ja: '公式天気', en: 'Official Weather', ko: '공식 날씨', zh: '官方天气' },
  
  // Stream warnings
  externalStreamOnly: { 
    ko: '이 스트림은 공식 사이트에서만 볼 수 있습니다', 
    en: 'This stream requires the official website', 
    ja: 'このストリームは公式サイトでのみ視聴できます',
    zh: '此直播只能在官方网站观看'
  },
  viewOnOfficialSite: { 
    ko: '공식 사이트에서 보기', 
    en: 'View on Official Site', 
    ja: '公式サイトで見る',
    zh: '在官网观看'
  },
  
  // Units
  meters: { ko: 'm', en: 'm', ja: 'm', zh: 'm' },
  degrees: { ko: '°', en: '°', ja: '°', zh: '°' },
  percent: { ko: '%', en: '%', ja: '%', zh: '%' },
  perHour: { ko: '명/시간', en: '/hr', ja: '/時', zh: '人/小时' },
  seats: { ko: '인승', en: 'seats', ja: '人乗り', zh: '座' },
  
  // Hero
  heroTitle: {
    ja: `${COUNTRY.ja}スキー場リアルタイム情報`,
    en: `Live ${ADJ.en} Ski Resort Intelligence`,
    ko: `${COUNTRY.ko} 스키장 실시간 정보`,
    zh: `${COUNTRY.zh}滑雪场实时信息`,
  },
  exploreWebcams: { ko: '웹캠 보기', en: 'Explore Webcams', ja: 'ウェブカメラを見る', zh: '浏览摄像头' },
  viewResorts: { ko: '리조트 보기', en: 'View Resorts', ja: 'リゾートを見る', zh: '查看度假村' },
  
  // Stats
  totalResorts: { ko: '스키장', en: 'Resorts', ja: 'スキー場', zh: '滑雪场' },
  totalWebcams: { ko: '웹캠', en: 'Webcams', ja: 'ウェブカメラ', zh: '摄像头' },
  totalSlopes: { ko: '코스', en: 'Runs', ja: 'コース', zh: '雪道' },
  totalLifts: { ko: '리프트', en: 'Lifts', ja: 'リフト', zh: '缆车' },
  
  // Regions
  regions: { ko: '지역', en: 'Regions', ja: '地域', zh: '地区' },
  allRegions: { ko: '전체 지역', en: 'All Regions', ja: 'すべての地域', zh: '全部地区' },
  
  // Footer
  copyright: {
    ja: `© 2025 ${SITE.brand.name}. All rights reserved.`,
    en: `© 2025 ${SITE.brand.name}. All rights reserved.`,
    ko: `© 2025 ${SITE.brand.name}. All rights reserved.`,
    zh: `© 2025 ${SITE.brand.name}. All rights reserved.`,
  },
  dataSource: {
    ja: 'データ: 各スキー場の公式サイト、Open-Meteo',
    en: 'Data from official resort websites and Open-Meteo',
    ko: '데이터: 각 스키장 공식 웹사이트, Open-Meteo',
    zh: '数据来源：各滑雪场官方网站、Open-Meteo',
  },

  // Dashboard
  liveCamera: { ko: '라이브 카메라', en: 'Live Camera', ja: 'ライブカメラ', zh: '实时摄像头' },
  liveCameras: { ko: '라이브 카메라', en: 'Live Cameras', ja: 'ライブカメラ', zh: '实时摄像头' },
  allWebcams: { ko: '전체 웹캠', en: 'All Webcams', ja: '全てのウェブカメラ', zh: '全部摄像头' },
  viewResortCameras: { ko: '리조트 카메라 보기', en: 'View resort cameras', ja: 'リゾートカメラを見る', zh: '查看度假村摄像头' },
  viewResortDetails: { ko: '리조트 상세 보기', en: 'View resort details', ja: 'リゾート詳細を見る', zh: '查看度假村详情' },
  clickForForecast: { ko: '클릭하여 7일 예보 보기', en: 'Click for 7-day forecast', ja: 'クリックして7日間予報を見る', zh: '点击查看7天预报' },
  fromTokyo: { ja: `${ORIGIN.ja}から`, en: `from ${ORIGIN.en}`, ko: `${ORIGIN.ko}에서`, zh: `距${ORIGIN.zh}` },
  windKmh: { ko: '바람 km/h', en: 'Wind km/h', ja: '風速 km/h', zh: '风速 km/h' },
  feelsLike: { ko: '체감', en: 'Feels like', ja: '体感', zh: '体感' },
  precipMm: { ko: '강수 mm', en: 'Precip mm', ja: '降水 mm', zh: '降水 mm' },
  humidity: { ko: '습도', en: 'Humidity', ja: '湿度', zh: '湿度' },
  visibility: { ko: '시정', en: 'Visibility', ja: '視程', zh: '能见度' },
  gusts: { ko: '돌풍', en: 'Gusts', ja: '突風', zh: '阵风' },
  totalRuns: { ko: '총 활주로', en: 'total runs', ja: '総コース', zh: '条雪道（总计）' },
  runs: { ko: '코스', en: 'runs', ja: 'コース', zh: '条雪道' },
  cams: { ko: '캠', en: 'cams', ja: 'カメラ', zh: '个摄像头' },
  // weather-proxy caches each Open-Meteo forecast for up to 6 hours (functions/api/weather-proxy.js).
  weatherNote: JP
    ? {
        ko: 'Open-Meteo API 날씨 데이터 • 최대 6시간 캐시. 공식 JMA 기상 정보는 클릭하세요.',
        en: 'Weather data from Open-Meteo API • Cached for up to 6 hours. Click for official JMA conditions.',
        ja: 'Open-Meteo API天気データ • 最大6時間キャッシュ。公式JMA気象情報はクリックしてください。',
        zh: '天气数据来自 Open-Meteo API • 最多缓存6小时。点击查看日本气象厅官方信息。',
      }
    : {
        ko: 'Open-Meteo API 날씨 데이터 • 최대 6시간 캐시. 공식 기상 정보는 클릭하세요.',
        en: 'Weather data from Open-Meteo API • Cached for up to 6 hours. Click for official conditions.',
        ja: 'Open-Meteo API天気データ • 最大6時間キャッシュ。公式気象情報はクリックしてください。',
        zh: '天气数据来自 Open-Meteo API • 最多缓存6小时。点击查看官方气象信息。',
      },
  raceTracksSection: { ko: '레이스 트랙', en: 'Race Tracks', ja: 'レーストラック', zh: '赛道' },
  trackLength: { ko: '트랙 길이', en: 'Track Length', ja: 'トラック長', zh: '赛道长度' },
  turns: { ko: '코너', en: 'Turns', ja: 'コーナー', zh: '弯道' },
  circuits: { ko: '서킷', en: 'circuits', ja: 'サーキット', zh: '赛道' },
  viewTrackCamera: { ko: '트랙 카메라 보기', en: 'View track camera', ja: 'トラックカメラを見る', zh: '查看赛道摄像头' },
  
  // Weather detail labels
  weatherFeels: { ko: '체감', en: 'Feels', ja: '体感', zh: '体感' },
  weatherWind: { ko: '풍속', en: 'Wind', ja: '風速', zh: '风速' },
  weatherPrecip: { ko: '강수', en: 'Precip', ja: '降水', zh: '降水' },
  weatherHumid: { ko: '습도', en: 'Humid', ja: '湿度', zh: '湿度' },
  weatherVis: { ko: '시정', en: 'Vis', ja: '視程', zh: '能见度' },
  weatherPressure: { ko: '기압', en: 'hPa', ja: '気圧', zh: '气压' },
  weatherGusts: { ko: '돌풍', en: 'Gusts', ja: '突風', zh: '阵风' },
  weatherRise: { ko: '일출', en: 'Rise', ja: '日出', zh: '日出' },
  weatherSet: { ko: '일몰', en: 'Set', ja: '日没', zh: '日落' },
  sevenDayForecast: { ko: '7일 예보', en: '7-Day Forecast', ja: '7日間予報', zh: '7天预报' },
  today: { ko: '오늘', en: 'Today', ja: '今日', zh: '今天' },
  
  // Racetrack specific
  racetrackLink: { ko: '레이스트랙 홈페이지', en: 'Racetrack Homepage', ja: 'レーストラックホームページ', zh: '赛道官网' },
  
  // Sorting
  sortBy: { ko: '정렬', en: 'Sort by', ja: '並び替え', zh: '排序方式' },
  sortByName: { ko: '이름순', en: 'Name (A-Z)', ja: '名前順', zh: '按名称' },
  sortBySnow: { ko: '적설량', en: 'Best Snow', ja: '積雪量', zh: '积雪最多' },
  sortBySlopes: { ko: '슬로프 수', en: 'Most Slopes', ja: 'ゲレンデ数', zh: '雪道最多' },
  sortByCameras: { ko: '카메라 수', en: 'Most Cameras', ja: 'カメラ数', zh: '摄像头最多' },
  sortByLength: { ko: '활주 거리', en: 'Longest Runs', ja: 'コース長', zh: '雪道最长' },
  sortByTemp: { ko: '낮은 기온', en: 'Coldest First', ja: '気温(低い順)', zh: '气温最低' },
  sortByConditions: { ko: '베스트 컨디션', en: 'Best Conditions', ja: 'ベストコンディション', zh: '最佳状况' },
  searchPlaceholder: { ko: '리조트 검색...', en: 'Search resorts...', ja: 'リゾートを検索...', zh: '搜索度假村...' },
  noResults: { ko: '검색 결과가 없습니다', en: 'No results found', ja: '検索結果がありません', zh: '没有找到结果' },
  
  // Detail pages
  allResorts: { ko: '전체 리조트', en: 'All Resorts', ja: '全リゾート', zh: '全部度假村' },
  backToDashboard: { ko: '대시보드로 돌아가기', en: 'Back to Dashboard', ja: 'ダッシュボードに戻る', zh: '返回仪表盘' },
  resortNotFound: { ko: '리조트를 찾을 수 없습니다', en: 'Resort Not Found', ja: 'リゾートが見つかりません', zh: '未找到度假村' },
  resortNotFoundDesc: { ko: '찾으시는 리조트가 존재하지 않습니다.', en: "The resort you're looking for doesn't exist.", ja: 'お探しのリゾートは存在しません。', zh: '您要查找的度假村不存在。' },
  racetrackNotFound: { ko: '레이스트랙을 찾을 수 없습니다', en: 'Racetrack Not Found', ja: 'レーストラックが見つかりません', zh: '未找到赛道' },
  racetrackNotFoundDesc: { ko: '찾으시는 레이스트랙이 존재하지 않습니다.', en: "The racetrack you're looking for doesn't exist.", ja: 'お探しのレーストラックは存在しません。', zh: '您要查找的赛道不存在。' },
  viewAllResorts: { ko: '전체 리조트 보기', en: 'View All Resorts', ja: '全リゾートを見る', zh: '查看全部度假村' },
  weatherUnavailable: { ko: '날씨 데이터를 사용할 수 없습니다', en: 'Weather data unavailable', ja: '天気データは利用できません', zh: '暂无天气数据' },
  total: { ko: '전체', en: 'total', ja: '合計', zh: '合计' },
  noWebcams: { ko: '사용 가능한 웹캠이 없습니다', en: 'No webcams available', ja: '利用可能なウェブカメラがありません', zh: '暂无可用摄像头' },
  likelyCovers: { ko: '예상 슬로프:', en: 'Likely covers:', ja: '対象ゲレンデ:', zh: '可能覆盖：' },
  stream: { ko: '스트림', en: 'Stream', ja: 'ストリーム', zh: '直播' },
  trackSpecs: { ko: '트랙 사양', en: 'Track Specifications', ja: 'トラック仕様', zh: '赛道规格' },
  mainTrackLength: { ko: '메인 트랙 길이', en: 'Main Track Length', ja: 'メイントラック長', zh: '主赛道长度' },
  capacity: { ko: '수용량', en: 'Capacity', ja: '輸送能力', zh: '运力' },
  speed: { ko: '속도', en: 'Speed', ja: '速度', zh: '速度' },
  racetrack: { ko: '레이스트랙', en: 'Racetrack', ja: 'レーストラック', zh: '赛道' },
  location: { ko: '위치', en: 'Location', ja: '場所', zh: '位置' },
  
  // SEO intro
  seoIntroHeading: {
    ja: `${COUNTRY.ja}のスキー場ライブカメラ・積雪予報・ゲレンデ状況`,
    en: `${COUNTRY.en} Ski Resort Webcams, Snow Forecasts & Live Conditions`,
    ko: `${COUNTRY.ko} 스키장 실시간 웹캠, 적설 예보, 슬로프 현황`,
    zh: `${COUNTRY.zh}滑雪场摄像头、降雪预报与实时状况`,
  },
  // Tooltips
  tooltipNaverMap: {
    ko: '네이버 지도에서 길찾기',
    en: 'Get directions on Naver Maps',
    ja: 'Naverマップで道順を見る',
    zh: '在 Naver 地图上查看路线'
  },
  tooltipKakaoMap: {
    ko: '카카오맵에서 길찾기',
    en: 'Get directions on Kakao Maps',
    ja: 'Kakaoマップで道順を見る',
    zh: '在 Kakao 地图上查看路线'
  },
  tooltipGoogleMap: {
    ko: '구글 지도에서 길찾기',
    en: 'Get directions on Google Maps',
    ja: 'Googleマップで道順を見る',
    zh: '在 Google 地图上查看路线'
  },
  tooltipWeather: JP
    ? {
    ko: '일본 기상청 공식 날씨 정보',
    en: 'Official JMA weather forecast',
    ja: '気象庁公式天気予報',
    zh: '日本气象厅官方天气预报'
  }
    : { ko: '공식 날씨 예보', en: 'Official weather forecast', ja: '公式天気予報', zh: '官方天气预报' },
  tooltipHomepage: {
    ko: '공식 홈페이지 방문',
    en: 'Visit official website',
    ja: '公式サイトを見る',
    zh: '访问官方网站'
  },
  tooltipLiveCams: {
    ko: '실시간 라이브 영상 수',
    en: 'Number of live camera feeds',
    ja: 'ライブカメラの数',
    zh: '实时摄像头数量'
  },
  tooltipSlopes: {
    ko: '스키/스노보드 슬로프 수',
    en: 'Number of ski/snowboard runs',
    ja: 'ゲレンデの数',
    zh: '滑雪/单板雪道数量'
  },
  tooltipTotalLength: {
    ko: '전체 슬로프 총 길이',
    en: 'Total combined run length',
    ja: '全ゲレンデの総延長',
    zh: '雪道总长度'
  },
  
  // Snow alerts
  currentlySnowing: {
    ko: '지금 눈이 오고 있어요!',
    en: "It's snowing now!",
    ja: '今、雪が降っています！',
    zh: '现在正在下雪！'
  },
  snowingToday: {
    ko: '오늘 눈 예보!',
    en: 'Snowing today!',
    ja: '今日は雪予報！',
    zh: '今天有雪！'
  },
  freshSnow: {
    ko: '마지막 강설: {days} 전',
    en: 'Last snow {days} ago',
    ja: '最後の降雪: {days}前',
    zh: '上次降雪：{days}前'
  },
  lastSnowfall: {
    ko: '마지막 강설: {days} 전',
    en: 'Last snow: {days} ago',
    ja: '最後の降雪: {days}前',
    zh: '上次降雪：{days}前'
  },
  noFreshSnow: {
    ko: '7일 넘게 신설 없음',
    en: 'No fresh snow in over 7 days',
    ja: '7日以上新雪なし',
    zh: '超过7天没有新雪'
  },
  snowDepth: {
    ko: '자연설',
    en: 'Natural Snow',
    ja: '自然雪',
    zh: '天然雪'
  },
  newSnow: {
    ko: '신설',
    en: 'New',
    ja: '新雪',
    zh: '新雪'
  },
  bestSnowConditions: {
    ko: '최고 설질',
    en: 'Best Snow Conditions',
    ja: '最高の雪質',
    zh: '最佳雪况'
  },
  bestSnowNow: {
    ko: '지금 가장 좋은 눈 상태',
    en: 'Best snow right now',
    ja: '今一番良い雪',
    zh: '当前雪况最好'
  },
  
  // Resort groups
  resortsInGroup: {
    ko: '개 리조트 연결',
    en: 'connected resorts',
    ja: '接続リゾート',
    zh: '个相连度假村'
  },
  connectedResorts: {
    ko: '연결된 리조트',
    en: 'Connected',
    ja: '接続',
    zh: '相连'
  },
  area: {
    ko: '에어리어',
    en: 'Area',
    ja: 'エリア',
    zh: '区域'
  },
};

// Display names for region keys that are not regions themselves (SiteConfig.regionLabels).
const regionTranslations: Record<string, Partial<LocalizedName>> = SITE.regionLabels ?? {};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: keyof Translations) => string;
  getName: (name: LocalizedName) => string;
  getRegion: (regionKey: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function detectBrowserLanguage(): Language {
  const browserLang = navigator.language.split('-')[0];
  if (isSiteLanguage(browserLang)) return browserLang;
  // English is the lingua franca fallback when the site offers it.
  return isSiteLanguage('en') ? 'en' : DEFAULT_LANGUAGE;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    // URL prefix is authoritative: a visitor (or crawler) landing on /ja/...
    // must render Japanese on the very first paint. Falling back to
    // localStorage/browser language here caused a redirect race that served
    // EN content on /ja and /ko URLs to fresh visitors.
    const urlLang = window.location.pathname.split('/')[1];
    if (isSiteLanguage(urlLang)) return urlLang;
    const saved = localStorage.getItem(SITE.storageKeys.language);
    if (isSiteLanguage(saved)) {
      return saved;
    }
    return detectBrowserLanguage();
  });

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem(SITE.storageKeys.language, lang);
  };

  const t = useCallback((key: keyof Translations): string => {
    return pick(translations[key], language);
  }, [language]);

  const getName = useCallback((name: LocalizedName): string => {
    return pick(name, language);
  }, [language]);

  const getRegion = useCallback((regionKey: string): string => {
    const translation = regionTranslations[regionKey];
    return translation ? pick(translation, language) : regionLabel(regionKey, language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t, getName, getRegion }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Custom hook to access language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
